<script lang="ts">
  import CategoryIcon from './CategoryIcon.svelte';

  export let app: { id: string; name: string; description: string; route: string; tags?: string[] };
  export let category: string = 'Other';
  export let index: number = 0;
  export let customizing: boolean = false;
  export let isPinned: boolean = false;
  export let isHidden: boolean = false;

  // Callbacks
  export let onTogglePin: (() => void) | null = null;
  export let onToggleHidden: (() => void) | null = null;
  export let onChangeCategory: (() => void) | null = null;

  // Category to CSS color variable mapping
  const catColors: Record<string, string> = {
    'PDF': 'var(--cat-pdf)',
    'Images': 'var(--cat-images)',
    'Audio/Video': 'var(--cat-av)',
    'Data': 'var(--cat-data)',
    'Text': 'var(--cat-text)',
    'QR': 'var(--cat-qr)',
    'Sharing': 'var(--cat-sharing)',
    'Games': 'var(--cat-games)',
    'Docs & Forms': 'var(--cat-docs)',
    'Privacy': 'var(--cat-privacy)',
    'Productivity': 'var(--cat-productivity)',
    'Other': 'var(--cat-other)',
  };

  $: catColor = catColors[category] || catColors['Other'];
</script>

<div
  class="tool-card"
  class:is-hidden={isHidden}
  class:is-pinned={isPinned && !customizing}
  style="--card-accent: {catColor}; animation-delay: {Math.min(index * 30, 300)}ms;"
>
  <a href={`#${app.route}`} class="card-link">
    <div class="card-header">
      <div class="card-icon-wrap">
        <CategoryIcon {category} size={20} />
      </div>
      {#if isPinned && !customizing}
        <span class="pin-indicator" title="Pinned">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
        </span>
      {/if}
    </div>
    <h3 class="card-title">{app.name}</h3>
    <p class="card-desc">{app.description}</p>
    <div class="card-footer">
      <span class="card-category-tag">{category}</span>
      <svg class="card-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
    </div>
  </a>

  {#if customizing}
    <div class="card-actions">
      {#if onTogglePin}
        <button class="action-btn" on:click|stopPropagation={onTogglePin}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill={isPinned ? 'currentColor' : 'none'} stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
          {isPinned ? 'Unpin' : 'Pin'}
        </button>
      {/if}
      {#if onToggleHidden}
        <button class="action-btn" on:click|stopPropagation={onToggleHidden}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            {#if isHidden}
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
            {:else}
              <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/>
            {/if}
          </svg>
          {isHidden ? 'Show' : 'Hide'}
        </button>
      {/if}
      {#if onChangeCategory}
        <button class="action-btn" on:click|stopPropagation={onChangeCategory}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
          Move
        </button>
      {/if}
    </div>
  {/if}
</div>

<style>
  .tool-card {
    position: relative;
    border-radius: var(--theme-radius-lg);
    background: hsl(var(--theme-card));
    border: 1px solid hsl(var(--theme-border) / 0.6);
    overflow: hidden;
    transition: all 300ms var(--theme-ease);
    height: 100%;
    display: flex;
    flex-direction: column;
    animation: stagger-in 0.4s var(--theme-ease) both;
  }
  .tool-card:hover {
    transform: translateY(-3px);
    box-shadow: var(--theme-shadow-md), 0 0 0 1px hsl(var(--card-accent) / 0.15);
    border-color: hsl(var(--card-accent) / 0.35);
  }
  .tool-card.is-hidden { opacity: 0.4; }

  .card-link {
    display: flex;
    flex-direction: column;
    text-decoration: none;
    color: inherit;
    padding: 1.25rem;
    flex: 1;
    gap: 0;
  }

  /* Card header with icon */
  .card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 0.875rem;
  }
  .card-icon-wrap {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: var(--theme-radius);
    background: hsl(var(--card-accent) / 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 300ms var(--theme-ease);
    flex-shrink: 0;
  }
  .tool-card:hover .card-icon-wrap {
    background: hsl(var(--card-accent) / 0.16);
    transform: scale(1.05);
  }
  .pin-indicator {
    color: hsl(var(--theme-accent));
    opacity: 0.7;
  }

  /* Typography */
  .card-title {
    font-family: 'Inter', sans-serif;
    font-size: 0.9375rem;
    font-weight: 600;
    color: hsl(var(--theme-foreground));
    margin-bottom: 0.375rem;
    letter-spacing: -0.015em;
    line-height: 1.3;
  }
  .card-desc {
    font-size: 0.8125rem;
    line-height: 1.55;
    color: hsl(var(--theme-muted-foreground));
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    flex: 1;
  }

  /* Footer with category + arrow */
  .card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 1rem;
    padding-top: 0.75rem;
    border-top: 1px solid hsl(var(--theme-border) / 0.3);
  }
  .card-category-tag {
    font-size: 0.6875rem;
    font-weight: 600;
    color: hsl(var(--card-accent));
    letter-spacing: 0.02em;
  }
  .card-arrow {
    color: hsl(var(--theme-muted-foreground) / 0.4);
    transition: all 300ms var(--theme-ease);
  }
  .tool-card:hover .card-arrow {
    color: hsl(var(--card-accent));
    transform: translateX(3px);
  }

  /* Customize actions */
  .card-actions {
    display: flex;
    gap: 0.375rem;
    padding: 0 1.25rem 1rem;
    flex-wrap: wrap;
  }
  .action-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    font-size: 0.6875rem;
    font-weight: 500;
    padding: 0.3rem 0.6rem;
    border-radius: var(--theme-radius-sm);
    border: 1px solid hsl(var(--theme-border));
    background: hsl(var(--theme-muted) / 0.1);
    color: hsl(var(--theme-muted-foreground));
    cursor: pointer;
    transition: all 200ms ease;
    font-family: 'Inter', sans-serif;
  }
  .action-btn:hover {
    background: hsl(var(--theme-muted) / 0.25);
    color: hsl(var(--theme-foreground));
    border-color: hsl(var(--theme-primary) / 0.3);
  }

  @media (max-width: 640px) {
    .card-link { padding: 1rem; }
    .card-icon-wrap { width: 2.25rem; height: 2.25rem; }
    .card-title { font-size: 0.875rem; }
    .card-desc { font-size: 0.8125rem; }
    .card-footer { margin-top: 0.75rem; padding-top: 0.625rem; }
  }
</style>
